import { useSelector, shallowEqual } from "react-redux";
import { get, pick } from "lodash";
import { randomUUID, safeSum } from "@frontend/utils";
const isValueNaN = (v) => {
  const value = typeof v === "string" ? parseFloat(v.replace(/[$,]/g, "")) : v;
  return Number.isNaN(value) || [false, null, void 0].includes(value);
};
const generateObjectDependencies = (object, nameOrPath) => {
  let valuesSelector = {};
  nameOrPath.map(
    ({ path, values }) => Array.isArray(values) && values.forEach((value) => {
      valuesSelector = {
        ...valuesSelector,
        [value]: path ? object[path]?.[value] : object[value] ?? null
      };
    })
  );
  return valuesSelector;
};
const loanDataFieldSelector = (nameOrPath, defaultValue = null) => (data) => {
  const loanInfo = data?.EPPS?.loanInfo || {};
  if (typeof nameOrPath === "string")
    return { value: get(loanInfo, nameOrPath) ?? defaultValue };
  if (Array.isArray(nameOrPath))
    return generateObjectDependencies(loanInfo, nameOrPath);
  return nameOrPath ? null : loanInfo;
};
const isShallowEqual = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};
const useLoanDataFieldSelectorHook = (pathValue, defaultValue) => useSelector(loanDataFieldSelector(pathValue, defaultValue), isShallowEqual);
const selectLoanDataField = (fieldName, defaultValue = null) => ({ EPPS }) => {
  const loanInfo = EPPS?.loanInfo || {};
  return get(loanInfo, fieldName, defaultValue);
};
const selectOriginalLoanInformation = (nameOrPath, defaultValue = null) => (store) => {
  const data = store?.EPPS?.originalLoanInformation || {};
  if (Array.isArray(nameOrPath)) {
    return pick(data, nameOrPath);
  }
  if (typeof nameOrPath === "string") {
    return get(data, nameOrPath, defaultValue);
  }
  return data;
};
const useLoanDataFieldSelector = (field, defaultValue = null) => useSelector(selectLoanDataField(field, defaultValue), shallowEqual);
const useOriginalLoanInformationSelector = (fieldName, defaultValue) => useSelector(
  selectOriginalLoanInformation(fieldName, defaultValue),
  isShallowEqual
);
const getDefaultFocusedDate = () => {
  const parsedCurrentDateValue = (/* @__PURE__ */ new Date()).toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });
  return parsedCurrentDateValue;
};
const formatTime = (date) => {
  const parsedDate = new Date(date);
  if (date) {
    let hours = parsedDate.getHours();
    let minutes = parsedDate.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours %= 12;
    if (hours === 0) {
      hours = 12;
    }
    if (hours < 10) {
      hours = `0${hours}`;
    }
    if (minutes < 10) {
      minutes = `0${minutes}`;
    }
    return `${hours}:${minutes} ${ampm}`;
  }
  return "";
};
const formatDate = (date) => {
  if (date) {
    const parsedDate = new Date(date);
    const year = parsedDate.getFullYear();
    const month = (1 + parsedDate.getMonth()).toString().padStart(2, "0");
    const day = parsedDate.getDate().toString().padStart(2, "0");
    return `${month}/${day}/${year}`;
  }
  return "";
};
const formatDateTime = (date) => {
  const formatedDate = formatDate(date);
  const formatedTime = formatTime(date);
  if (formatedDate !== "" && formatedTime !== "")
    return `${formatedDate} ${formatedTime}`;
  return "";
};
const selectLookups = (nameOrPath, defaultValue = null) => (state) => {
  const data = state?.eppsData?.lookups?.data || {};
  if (Array.isArray(nameOrPath)) {
    return pick(data, nameOrPath);
  }
  if (typeof nameOrPath === "string") {
    return get(data, nameOrPath, defaultValue);
  }
  return data;
};
const useLookup = (lookup, defaultValue) => useSelector(selectLookups(lookup, defaultValue), shallowEqual);
const handleTotalAmountValue = (schemaForm, value) => {
  const amounts = schemaForm.getValues([
    "loanInformation.firstMortgageAmount",
    "loanInformation.feeAmountFinanced",
    "loanInformation.financedAmount"
  ]);
  const totalValue = calculateTotalLoanAmount(
    amounts[0],
    amounts[1] ?? amounts[2]
  );
  return totalValue;
};
const parseCompensationValue = (value) => {
  let getValue = null;
  if (value) {
    if (value === "Borrower-Paid") {
      getValue = 1;
    } else {
      getValue = 2;
    }
  }
  return getValue;
};
const inRange = (a, b, c) => !Number.isNaN(parseFloat(a)) && a >= b && a <= c;
const clearNoCreditScoresIfValid = (num, noCreditScores, schemaForm) => {
  if (inRange(num, 300, 850) && noCreditScores) {
    schemaForm.setValue("borrowerFinancialHistory.noCreditScores", false);
  }
};
const calculateTotalLoanAmount = (firstMortgageAmount, financedAmount) => {
  let result = 0;
  const ParsedLoanAmount = parseInt(firstMortgageAmount, 10) || 0;
  const ParsedFinancedAmount = parseInt(financedAmount, 10) || 0;
  result = safeSum(ParsedLoanAmount, Math.floor(ParsedFinancedAmount));
  return result;
};
const evaluateRule = (rule, loanInfo, ruleSet, preview) => {
  const getValue = (obj, path) => {
    return path.split(".").reduce((o, key) => o ? o[key] : void 0, obj);
  };
  function evaluateCondition(condition) {
    const { op, params } = condition;
    const referencedRule = ruleSet.find((r) => r.id === op);
    if (referencedRule) {
      return evaluateRule(referencedRule, loanInfo, ruleSet, preview);
    }
    const normalizeValues = (currentValue2, targetValue2) => {
      let normalizedCurrentValue2 = currentValue2;
      let normalizedTargetValue2 = targetValue2;
      if (Array.isArray(currentValue2)) {
        normalizedCurrentValue2 = currentValue2.map(String);
      }
      if (typeof currentValue2 === "boolean" || typeof currentValue2 === "number") {
        normalizedCurrentValue2 = String(currentValue2);
      }
      if (typeof targetValue2 === "boolean" || typeof targetValue2 === "number") {
        normalizedTargetValue2 = String(targetValue2);
      }
      return [normalizedCurrentValue2, normalizedTargetValue2];
    };
    const currentValue = getValue(loanInfo, params[0]);
    const targetValue = params[1];
    const [normalizedCurrentValue, normalizedTargetValue] = normalizeValues(
      currentValue,
      targetValue
    );
    switch (op) {
      case "isEmptyField":
        return !normalizedCurrentValue;
      case "isNotEmptyField":
        return Boolean(normalizedCurrentValue);
      case "isFieldEqualToValue":
        return Array.isArray(normalizedCurrentValue) ? normalizedCurrentValue.includes(normalizedTargetValue) : normalizedCurrentValue === normalizedTargetValue;
      case "isFieldNotEqualToValue":
        return Array.isArray(normalizedCurrentValue) ? !normalizedCurrentValue.includes(normalizedTargetValue) : normalizedCurrentValue !== normalizedTargetValue;
      case "isFieldMoreThanValue":
        return currentValue > targetValue;
      case "isFieldLessThanValue":
        return currentValue < targetValue;
      case "isFieldMoreThanOrEqualToValue":
        return currentValue >= targetValue;
      case "isFieldLessThanOrEqualToValue":
        return currentValue <= targetValue;
      default:
        throw new Error(`Unknown operation: ${op}`);
    }
  }
  const evaluateLogicalCondition = (condition) => {
    if (!condition || !condition.op || !condition.params) return false;
    const results = condition.params.map(evaluateCondition);
    return condition.op === "and" ? results.every(Boolean) : results.some(Boolean);
  };
  return rule.instructions.every(
    (instruction) => evaluateLogicalCondition(instruction.condition)
  );
};
const evaluateRuleById = (ruleId, loanInfo, ruleSet, preview) => {
  const rule = ruleSet.find((r) => r.id === ruleId);
  if (!rule) return true;
  return evaluateRule(rule, loanInfo, ruleSet, preview);
};
const evaluateValueRuleById = (ruleId, loanInfo, ruleSet, preview) => {
  const rule = ruleSet.find((r) => r.id === ruleId);
  if (!rule) return { matched: false };
  let result = { matched: false };
  for (const instruction of rule.instructions) {
    const tempRule = { ...rule, instructions: [instruction] };
    const conditionMet = evaluateRule(tempRule, loanInfo, ruleSet, preview);
    if (conditionMet && instruction.action?.length) {
      result = { matched: true, value: instruction.action[0].value };
    }
  }
  return result;
};
const useUserData = () => useSelector(({ EPPS: { userData = {} } }) => {
  return userData;
});
const lookupsSelector = (lookupName) => (store) => {
  const data = store?.eppsData?.lookups?.data || {};
  if (lookupName) {
    const getName = String(lookupName).replace(/[0-9]/g, "").replace("-", "");
    return get(data, getName);
  }
  return lookupName ? [] : data;
};
const useLookupOptions = (lookupName) => useSelector(lookupsSelector(lookupName));
const rawLookupsSelector = (lookupName) => (store) => {
  const rawData = store?.eppsData?.lookups?.raw || {};
  if (lookupName) {
    const getName = String(lookupName).replace(/[0-9]/g, "").replace(/-/g, "");
    return get(rawData, getName) || [];
  }
  return rawData;
};
const useRawLookupOptions = (lookupName) => useSelector(rawLookupsSelector(lookupName));
const optionBody = ({ id, lookup, ...rest }) => ({
  dsId: `${lookup}-${id || "none"}`,
  type: "option",
  ...rest
});
const transformOfficeUsers = (officeUsers) => {
  const result = [];
  let currentOffice = null;
  officeUsers.forEach((obj) => {
    if (obj.office !== currentOffice) {
      result.push({
        dsId: `officeUsers-${randomUUID() || "none"}`,
        type: "section",
        label: obj.office
      });
      currentOffice = obj.office;
    }
    result.push(obj);
  });
  return result;
};
const generateOfficeUsersComboboxOptionsWithItemLayout = (lookup = "", options = []) => {
  const comboboxOptions = Array.isArray(options) && options.map((item) => {
    const extraProps = {};
    const label = `${item.firstName} ${item.lastName}`;
    const value = item.userId;
    const { login } = item;
    const { office } = item;
    const officerFormat = { label, value, login, office, id: randomUUID() };
    return optionBody({ lookup, ...extraProps, ...officerFormat });
  });
  return transformOfficeUsers(comboboxOptions) || [];
};
const zeroPadCensusTract = (num = "") => {
  if (num && !Number.isNaN(num)) {
    const places = 7;
    const parseNum = Number(num).toFixed(2);
    return parseNum.padStart(places, "0");
  }
  return "";
};
const getUpfrontPPEFeeValue = (customDependencies, other) => {
  const govtUpfrontFees = other.govtUpfrontFees || [];
  const valueMapping = {
    2: "FHA",
    // FHA
    3: "VA",
    // va
    4: "USDA"
    // USDA
  };
  const standardProducts = customDependencies?.standardProducts || [];
  const specialProducts = customDependencies?.specialProducts || [];
  const latestStandardProduct = standardProducts.find(
    (product) => valueMapping[product]
  );
  if (latestStandardProduct === 3) return getVAFeeAmount(other);
  if (latestStandardProduct === 4) return 1;
  if (specialProducts?.includes(136) && latestStandardProduct !== 4) return 1;
  const upfrontFee = govtUpfrontFees.find(
    (fee) => fee.feeName === valueMapping[latestStandardProduct]
  );
  if (upfrontFee?.value) return upfrontFee.value;
  return 0;
};
const isGovtLoan = (standardProducts, specialProducts) => {
  const standardGov = standardProducts?.includes(2) || standardProducts?.includes(3) || standardProducts?.includes(4);
  const specialGov = specialProducts?.includes(136);
  return standardGov || specialGov;
};
const applyRecursiveRate = (rate) => {
  const recursiveMultiplier = rate / 100;
  const firstRecursive = rate * recursiveMultiplier;
  const secondRecursive = firstRecursive * recursiveMultiplier;
  const thirdRecursive = secondRecursive * recursiveMultiplier;
  const recursiveRate = rate + firstRecursive + secondRecursive + thirdRecursive;
  return recursiveRate;
};
const getVAFeeAmount = (dependencies) => {
  const loanPurposeMap = {
    1: "Purchase",
    2: "VA IRRRL",
    3: "C/O Refinance",
    4: "Construction",
    5: "Construction-Perm"
  };
  const { loanPurpose, vaFirstTimeUse, ltv } = dependencies;
  const govtUpfrontFees = dependencies.govtUpfrontFees || [];
  if (govtUpfrontFees && govtUpfrontFees.length === 0) return 0;
  const vaFilteredValues = govtUpfrontFees.filter(
    (fee) => fee.feeName === loanPurposeMap[loanPurpose]
  );
  if (!vaFilteredValues || vaFilteredValues.length === 0) return 0;
  const downPaymentPerc = convertLTVToDownPayment(ltv);
  let vaIsInRange = [];
  if (loanPurpose === 2 || loanPurpose === 3)
    vaIsInRange.push(vaFilteredValues[0]);
  else
    vaIsInRange = vaFilteredValues.filter(
      (fee) => downPaymentPerc >= fee.minDown && downPaymentPerc < fee.maxDown
    );
  if (!vaIsInRange || vaIsInRange.length === 0) return 0;
  return vaFirstTimeUse ? vaIsInRange[0]?.firstUse : vaIsInRange[0]?.afterFirstUse;
};
const convertLTVToDownPayment = (ltv) => {
  return 100 - ltv || 0;
};
const getClosestUSDAGuaranteeFee = (value) => {
  const guaranteeFees = [
    0.5,
    1,
    1.25,
    1.5,
    1.75,
    2,
    2.25,
    2.5,
    2.75,
    3,
    3.25,
    3.5,
    3.75,
    4
  ];
  return guaranteeFees.reduce(
    (prev, curr) => Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
  );
};
const getLastName = (option) => {
  if (option.lastName !== null && option.lastName !== void 0 && option.lastName !== "") {
    const valueType = typeof option.lastName;
    if (valueType === "string" || valueType === "number") {
      if (valueType === "number" && (!isFinite(option.lastName) || Number.isNaN(option.lastName))) {
      } else {
        const lastNameStr = String(option.lastName).trim();
        if (lastNameStr) return lastNameStr.toLowerCase();
      }
    }
  }
  const parts = (option.label || "").trim().split(/\s+/);
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : (option.label || "").toLowerCase();
};
const getFirstName = (option) => {
  if (option.firstName !== null && option.firstName !== void 0 && option.firstName !== "") {
    const valueType = typeof option.firstName;
    if (valueType === "string" || valueType === "number") {
      if (valueType === "number" && (!isFinite(option.firstName) || Number.isNaN(option.firstName))) {
      } else {
        const firstNameStr = String(option.firstName).trim();
        if (firstNameStr) return firstNameStr.toLowerCase();
      }
    }
  }
  const parts = (option.label || "").trim().split(/\s+/);
  return parts.length > 1 ? parts.slice(0, -1).join(" ").toLowerCase() : "";
};
const formatLabel = (option) => {
  if (option.type === "section") return option.label;
  let firstName;
  let lastName;
  const hasFirstName = option.firstName !== null && option.firstName !== void 0 && option.firstName !== "";
  const hasLastName = option.lastName !== null && option.lastName !== void 0 && option.lastName !== "";
  if (hasFirstName && hasLastName) {
    firstName = String(option.firstName).trim().toLowerCase();
    lastName = String(option.lastName).trim().toLowerCase();
  } else {
    const parts = (option.label || "").trim().split(/\s+/);
    if (parts.length > 1) {
      lastName = parts[parts.length - 1].toLowerCase();
      firstName = parts.slice(0, -1).join(" ").toLowerCase();
    } else {
      return option.label;
    }
  }
  if (firstName && lastName) {
    const formattedFirst = firstName.split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
    const formattedLast = lastName.charAt(0).toUpperCase() + lastName.slice(1);
    return `${formattedLast} ${formattedFirst}`;
  }
  return option.label;
};
const alphabetizeByLoanOfficer = (options) => {
  const officeMap = {};
  const sections = {};
  for (const option of options) {
    if (option.type === "section") {
      sections[option.label] = option;
    } else {
      const officeKey = option.office || "Unassigned";
      (officeMap[officeKey] ??= []).push(option);
    }
  }
  for (const officers of Object.values(officeMap)) {
    const sortKeys = new Map(officers.map((o) => [o, getLastName(o)]));
    officers.sort((a, b) => sortKeys.get(a).localeCompare(sortKeys.get(b)));
  }
  const sortedOffices = Object.keys(officeMap).sort(
    (a, b) => a.localeCompare(b, void 0, { sensitivity: "base" })
  );
  const result = [];
  for (const office of sortedOffices) {
    result.push(
      sections[office] ?? {
        type: "section",
        label: office,
        dsId: `section-${office}`
      }
    );
    for (const option of officeMap[office]) {
      result.push({ ...option, label: formatLabel(option) });
    }
  }
  return result;
};
const getEncFeeFactor = (originalLoanInformation, ppeGovtFeeFactor) => {
  const isFHA = originalLoanInformation.mortgageType === "FHA";
  const fundingAmount = originalLoanInformation.rateLock?.fundingAmount;
  const baseLoanAmount = originalLoanInformation.rateLock?.baseLoanAmount;
  const encFactor = originalLoanInformation.mortgageInsurancePremiumUpfrontFactorPercent;
  const tolerance = 0.01;
  const amountFactor = fundingAmount / baseLoanAmount * 100;
  if (isFHA) {
    const isOverride = Math.abs(ppeGovtFeeFactor - encFactor) > tolerance;
    const fundingAmountContradictsEncFactor = Number.isFinite(amountFactor) && Math.abs(amountFactor - encFactor) > tolerance;
    return isOverride && !fundingAmountContradictsEncFactor ? encFactor : null;
  }
  return Math.abs(amountFactor - encFactor) < tolerance ? encFactor : null;
};
export {
  alphabetizeByLoanOfficer,
  applyRecursiveRate,
  calculateTotalLoanAmount,
  clearNoCreditScoresIfValid,
  evaluateRuleById,
  evaluateValueRuleById,
  formatDate,
  formatDateTime,
  formatLabel,
  formatTime,
  generateObjectDependencies,
  generateOfficeUsersComboboxOptionsWithItemLayout,
  getClosestUSDAGuaranteeFee,
  getDefaultFocusedDate,
  getEncFeeFactor,
  getFirstName,
  getLastName,
  getUpfrontPPEFeeValue,
  handleTotalAmountValue,
  inRange,
  isGovtLoan,
  isShallowEqual,
  isValueNaN,
  loanDataFieldSelector,
  optionBody,
  parseCompensationValue,
  selectLoanDataField,
  selectLookups,
  selectOriginalLoanInformation,
  transformOfficeUsers,
  useLoanDataFieldSelector,
  useLoanDataFieldSelectorHook,
  useLookup,
  useLookupOptions,
  useOriginalLoanInformationSelector,
  useRawLookupOptions,
  useUserData,
  zeroPadCensusTract
};
